import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { browserTimeZone } from "../../lib/dates";
import type { CheckoutPricing, TenantSettings } from "../../lib/types";

interface SettingsResponse {
	settings: TenantSettings;
}

export const useSettings = () =>
	useQuery({
		queryKey: ["settings"],
		queryFn: () => api.get<SettingsResponse>("/api/settings"),
	});

export const useUpdateSettings = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (patch: Partial<TenantSettings>) =>
			api.put<SettingsResponse>("/api/settings", patch),
		onSuccess: (data) => queryClient.setQueryData(["settings"], data),
	});
};

/** Moves every live campaign's pricing in Shopify, so it is never part of an ordinary save. */
export const useSwitchCheckoutPricing = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (mode: CheckoutPricing) =>
			api.put<SettingsResponse>("/api/settings/checkout-pricing", { mode }),
		onSuccess: (data) => queryClient.setQueryData(["settings"], data),
		// A failed switch may have restored the previous mode server side.
		onError: () => queryClient.invalidateQueries({ queryKey: ["settings"] }),
	});
};

/**
 * Schedules are read and written in the shop's time zone. Until one is saved in
 * Settings the browser's stands in, and the schedule says so.
 */
export const useShopTimeZone = (): { timeZone: string; fromSettings: boolean } => {
	const saved = useSettings().data?.settings.timezone;
	return saved
		? { timeZone: saved, fromSettings: true }
		: { timeZone: browserTimeZone(), fromSettings: false };
};
